import * as Tooltip from '@radix-ui/react-tooltip';
import { Link, useRouterState } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { Activity, Clock, Element3, Link21, NotificationBing, Ruler, type Icon } from 'iconsax-reactjs';
import { cn } from '@/lib/utils';
import { useI18n, type TranslationKey } from '@/lib/i18n';
import { MODULE_IMPORTANCE, type ModuleId } from '@pipyar/core';
import { GapIcon } from '@/components/GapIcon';
import { VolumeIcon } from '@/components/VolumeIcon';

export interface DockItem {
  /** Route path this item navigates to. */
  to: string;
  key: TranslationKey;
  icon: Icon;
}

/**
 * The dock's contents: analysis modules only.
 *
 * Settings deliberately lives in the top bar instead. The dock is for places you
 * move between while working; configuration is something you visit occasionally
 * and belongs with the other chrome.
 */
const MODULE_ITEMS: (DockItem & { module: ModuleId })[] = [
  { to: '/fibonacci', key: 'nav.fibonacci', icon: Ruler, module: 'fibonacci' },
  { to: '/gaps', key: 'nav.gaps', icon: GapIcon, module: 'gaps' },
  { to: '/rsi', key: 'nav.rsi', icon: Activity, module: 'rsi' },
  { to: '/time-windows', key: 'nav.timeWindows', icon: Clock, module: 'points' },
  { to: '/knots', key: 'nav.knots', icon: Link21, module: 'knots' },
  { to: '/volume', key: 'nav.volume', icon: VolumeIcon, module: 'volume' },
];

/**
 * Dashboard first, then the modules by MODULE_IMPORTANCE.
 *
 * The dashboard is pinned rather than ranked: it is not a module, it is the
 * place you come back to. Everything after it is ordered by the same weights
 * the dashboard tiles use, so the two never disagree about which module matters
 * most — and sorting from the shared constant means adding a module or changing
 * a weight moves both at once.
 */
export const DOCK_ITEMS: DockItem[] = [
  { to: '/', key: 'nav.dashboard', icon: Element3 },
  ...[...MODULE_ITEMS].sort(
    (a, b) => MODULE_IMPORTANCE[b.module] - MODULE_IMPORTANCE[a.module],
  ),
  // Last and unranked: price alerts are a tool, not an analysis module, so
  // they carry no importance weight and have no dashboard tile.
  { to: '/alerts', key: 'nav.alerts', icon: NotificationBing },
];

function DockButton({ item, active }: { item: DockItem; active: boolean }) {
  const IconComponent = item.icon;
  const { t } = useI18n();
  const label = t(item.key);

  return (
    <Tooltip.Root delayDuration={250}>
      <Tooltip.Trigger asChild>
        <Link
          to={item.to}
          aria-label={label}
          aria-current={active ? 'page' : undefined}
          className={cn(
            'group relative flex size-12 items-center justify-center rounded-xl',
            'outline-none transition-colors duration-200',
            'focus-visible:ring-2 focus-visible:ring-ring',
            active
              ? 'text-primary'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {/* The active pill sits behind the icon and animates between items via a
              shared layoutId, so switching sections slides rather than blinks. */}
          {active && (
            <motion.span
              layoutId="dock-active"
              className="absolute inset-0 rounded-xl bg-primary/15 shadow-[0_0_18px_-4px_color-mix(in_oklch,var(--primary)_65%,transparent)] ring-1 ring-primary/40"
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            />
          )}
          <motion.span
            className="relative"
            whileHover={{ y: -2, scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            transition={{ type: 'spring', stiffness: 420, damping: 24 }}
          >
            {/* One variant, always. Swapping Linear -> Bold on activation
                redraws some Iconsax glyphs at noticeably different weights and
                proportions, so the icon appeared to change rather than simply
                light up. Active state is already carried by the colour, the
                pill behind it and the dot below. */}
            <IconComponent size={23} variant="Linear" />
          </motion.span>

          {/* Small dot under the active item, the usual dock affordance. */}
          <span
            className={cn(
              'absolute -bottom-0.5 size-1 rounded-full bg-primary transition-opacity duration-200',
              active ? 'opacity-100' : 'opacity-0',
            )}
          />
        </Link>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          sideOffset={12}
          className={cn(
            'z-50 rounded-md border border-border bg-elevated px-2.5 py-1.5',
            'text-xs font-medium text-foreground shadow-lg',
            'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
          )}
        >
          {label}
          <Tooltip.Arrow className="fill-[var(--elevated)]" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

export function Dock() {
  // Subscribe to just the pathname so the dock re-renders on navigation only.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { t } = useI18n();

  return (
    <Tooltip.Provider>
      <nav
        aria-label={t('nav.main')}
        // pb env() keeps the dock above the iOS home indicator.
        className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 26, delay: 0.1 }}
          className={cn(
            // Glass (see index.css), lifted further off the page than a card.
            'glass pointer-events-auto flex items-center gap-1 rounded-2xl p-2 shadow-2xl',
          )}
        >
          {DOCK_ITEMS.map((item) => (
            <DockButton
              key={item.to}
              item={item}
              active={
                item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)
              }
            />
          ))}
        </motion.div>
      </nav>
    </Tooltip.Provider>
  );
}
